/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect} from "react"
import {KlassappTable} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {KlassappTableProps} from "types/common"
import styles from "./PaymentPlanItemTable.module.css"
import {useModel} from "hooks"
import {formatCurrency, handleError} from "helpers"
import {useTranslation} from "react-i18next"
import {studentPaymentService} from "services"
import {PaymentProvider, RecurringPaymentStatus, StudentRecurringPayment} from "types/student-account/student-payment"
import moment from "moment"

type Props = KlassappTableProps & {
    profileId: number
    paymentPlanId: number
    reloadKey?: number
}

const PaymentPlanRecurringPaymentsTable = ({
    columns,
    data,
    allFields,
    fields,
    menuActions,
    isLoading,
    isHideMenuActions,
    dispatch,
    dispatchFunc,
    isLoadedTableFuncs,
    onChangeFields,
    onDraggableColumn,
    onChangeAllFields,
    onUpdateRowData,
    onUpdateTableData,
    onClickRowItem: _onClickRowItem,
    profileId,
    paymentPlanId,
    reloadKey
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()

    const getData = useCallback(async () => {
        try {
            dispatch({isLoading: true})
            const all = await studentPaymentService.getStudentRecurringPayments(profileId)
            const linked = all.filter((item) => item.paymentPlanItems?.some((pi) => pi.paymentPlanId === paymentPlanId))
            dispatch({total: linked.length, data: linked.map((item) => ({...item, id: item.recurringPaymentId}))})
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }, [profileId, paymentPlanId])

    const pauseRecurringPayment = useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.pauseStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData]
    )

    const unpauseRecurringPayment = useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.unpauseStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData]
    )

    const cancelRecurringPayment = useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.cancelStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData]
    )

    const resumeRecurringPayment = useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.resumeStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData]
    )

    const syncRecurringPayment = useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.syncStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData]
    )

    const deleteRecurringPayment = useCallback(
        async (item: StudentRecurringPayment) => {
            try {
                dispatch({isLoading: true})
                await studentPaymentService.deleteStudentRecurringPayment(item.recurringPaymentId)
                getData()
            } catch (error) {
                handleError(error)
            } finally {
                dispatch({isLoading: false})
            }
        },
        [getData]
    )

    const getFields = useCallback(
        () => [
            t("column.id"),
            t("column.status"),
            t("column.amount"),
            t("column.interval"),
            t("column.lastBilledDate"),
            t("column.nextBillingDate"),
            t("column.cancelAt")
        ],
        [t]
    )

    const getColumns = useCallback(
        () => [
            {
                title: t("column.id"),
                field: "recurringPaymentId",
                columnIndex: 1
            },
            {
                title: t("column.status"),
                field: "status",
                columnIndex: 2
            },
            {
                title: t("column.amount"),
                field: "amountInCents",
                columnIndex: 3,
                render: (value, record: StudentRecurringPayment) => formatCurrency(value / 100, record.currency)
            },
            {
                title: t("column.interval"),
                field: "interval",
                columnIndex: 4
            },
            {
                title: t("column.lastBilledDate"),
                field: "lastBilledAt",
                columnIndex: 5,
                render: (value) => value && moment(value).format(userDateFormat)
            },
            {
                title: t("column.nextBillingDate"),
                field: "nextBillingAt",
                columnIndex: 6,
                render: (value) => value && moment(value).format(userDateFormat)
            },
            {
                title: t("column.cancelAt"),
                field: "cancelAt",
                columnIndex: 7,
                render: (value) => value && moment(value).format(userDateFormat)
            }
        ],
        [t, userDateFormat]
    )

    const getMenuActions = useCallback(
        () => [
            {
                title: t("label.pause", "Pause"),
                icon: "PAUSE",
                action: pauseRecurringPayment,
                canShow: (item: StudentRecurringPayment) =>
                    item.status === RecurringPaymentStatus.Active &&
                    (item.provider === PaymentProvider.Stripe || item.provider === PaymentProvider.Paypal)
            },
            {
                title: t("label.unpause", "Unpause"),
                icon: "RESUME",
                action: unpauseRecurringPayment,
                canShow: (item: StudentRecurringPayment) =>
                    item.status === RecurringPaymentStatus.Paused &&
                    (item.provider === PaymentProvider.Stripe || item.provider === PaymentProvider.Paypal)
            },
            {
                title: t("label.cancel"),
                icon: "EDIT",
                action: cancelRecurringPayment,
                canShow: (item: StudentRecurringPayment) => !item.cancelAt
            },
            {
                title: t("label.resume"),
                icon: "RESUME",
                action: resumeRecurringPayment,
                canShow: (item: StudentRecurringPayment) => item.status === RecurringPaymentStatus.Canceled
            },
            {
                title: t("label.sync", "Sync"),
                icon: "REFRESH",
                action: syncRecurringPayment,
                canShow: () => true
            },
            {
                title: t("label.delete"),
                icon: "DELETE",
                action: deleteRecurringPayment,
                canShow: () => true
            }
        ],
        [
            t,
            pauseRecurringPayment,
            unpauseRecurringPayment,
            cancelRecurringPayment,
            resumeRecurringPayment,
            syncRecurringPayment,
            deleteRecurringPayment
        ]
    )

    const onClickRowItem = useCallback(() => {}, [])
    const getTableHeaderActions = useCallback(() => [], [])

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: () => ""},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getMenuActions", func: getMenuActions},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getListData", func: getData},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, reloadKey])

    return (
        <div className={styles.wrap}>
            <h3 className={styles.title}>Recurring Payments</h3>
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                menuActions={isHideMenuActions ? [] : menuActions}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn={false}
                onClickRowItem={onClickRowItem}
                onChangeFields={onChangeFields}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
            />
        </div>
    )
}

export default KlassappTableHOC(PaymentPlanRecurringPaymentsTable)
